import { cloneChamferFeatureDefinitionData } from './cloneBlendFeatureDefinitionData';

/**
 * 倒角字段可用性。
 */
export type ChamferPropertyFieldAvailability = 'enabled' | 'disabled' | 'hidden';

/**
 * 倒角属性栏字段。
 */
export interface ChamferPropertyFieldDescriptor {
  /** 字段是否可见或可编辑。 */
  availability: ChamferPropertyFieldAvailability;
  /** 字段是否属于本阶段明确忽略的 SolidWorks 选项。 */
  ignored: boolean;
}

/**
 * 倒角属性栏描述。
 */
export interface ChamferPropertyDescriptor {
  /** 倒角类型。 */
  type: ChamferType;
  /** 字段描述集合。 */
  fields: Record<ChamferPropertyFieldKey, ChamferPropertyFieldDescriptor>;
}

/**
 * 倒角属性栏字段键。
 */
export type ChamferPropertyFieldKey =
  | 'tangentPropagation'
  | 'partialEdgeParameters'
  | 'flipDirection'
  | 'vertexDistances'
  | 'helpPoint'
  | 'faceSetSelection'
  | 'faceFaceModes'
  | 'keepFeatures'
  | 'selectThroughFaces';

const ENABLED: ChamferPropertyFieldAvailability = 'enabled';
const HIDDEN: ChamferPropertyFieldAvailability = 'hidden';

/**
 * 创建普通字段描述。
 *
 * @param availability 字段可用性。
 * @returns 字段描述。
 */
function field(availability: ChamferPropertyFieldAvailability): ChamferPropertyFieldDescriptor {
  return {
    availability,
    ignored: false,
  };
}

/**
 * 创建忽略字段描述。
 *
 * @returns 忽略字段描述。
 */
function ignoredField(): ChamferPropertyFieldDescriptor {
  return {
    availability: HIDDEN,
    ignored: true,
  };
}

/**
 * 倒角属性栏描述表。
 */
const CHAMFER_PROPERTY_DESCRIPTORS: Record<ChamferType, ChamferPropertyDescriptor> = {
  'angle-distance': {
    type: 'angle-distance',
    fields: {
      tangentPropagation: ignoredField(),
      partialEdgeParameters: field(HIDDEN),
      flipDirection: field(ENABLED),
      vertexDistances: field(HIDDEN),
      helpPoint: ignoredField(),
      faceSetSelection: field(HIDDEN),
      faceFaceModes: field(HIDDEN),
      keepFeatures: ignoredField(),
      selectThroughFaces: ignoredField(),
    },
  },
  'distance-distance': {
    type: 'distance-distance',
    fields: {
      tangentPropagation: ignoredField(),
      partialEdgeParameters: field(HIDDEN),
      flipDirection: field(HIDDEN),
      vertexDistances: field(HIDDEN),
      helpPoint: ignoredField(),
      faceSetSelection: field(HIDDEN),
      faceFaceModes: field(HIDDEN),
      keepFeatures: ignoredField(),
      selectThroughFaces: ignoredField(),
    },
  },
  vertex: {
    type: 'vertex',
    fields: {
      tangentPropagation: ignoredField(),
      partialEdgeParameters: field(HIDDEN),
      flipDirection: field(HIDDEN),
      vertexDistances: field(ENABLED),
      helpPoint: ignoredField(),
      faceSetSelection: field(HIDDEN),
      faceFaceModes: field(HIDDEN),
      keepFeatures: ignoredField(),
      selectThroughFaces: ignoredField(),
    },
  },
  'offset-face': {
    type: 'offset-face',
    fields: {
      tangentPropagation: ignoredField(),
      partialEdgeParameters: field(ENABLED),
      flipDirection: field(ENABLED),
      vertexDistances: field(HIDDEN),
      helpPoint: ignoredField(),
      faceSetSelection: field(HIDDEN),
      faceFaceModes: field(HIDDEN),
      keepFeatures: ignoredField(),
      selectThroughFaces: ignoredField(),
    },
  },
  'face-face': {
    type: 'face-face',
    fields: {
      tangentPropagation: ignoredField(),
      partialEdgeParameters: field(HIDDEN),
      flipDirection: field(ENABLED),
      vertexDistances: field(HIDDEN),
      helpPoint: ignoredField(),
      faceSetSelection: field(ENABLED),
      faceFaceModes: field(ENABLED),
      keepFeatures: ignoredField(),
      selectThroughFaces: ignoredField(),
    },
  },
};

/**
 * 获取倒角属性栏描述。
 *
 * @param type 倒角类型。
 * @returns 倒角属性栏描述。
 */
export function getChamferPropertyDescriptor(type: ChamferType): ChamferPropertyDescriptor {
  return clonePlainDescriptor(CHAMFER_PROPERTY_DESCRIPTORS[type]);
}

/**
 * 克隆属性栏描述。
 *
 * @param descriptor 属性栏描述。
 * @returns 克隆后的描述。
 */
function clonePlainDescriptor(descriptor: ChamferPropertyDescriptor): ChamferPropertyDescriptor {
  return {
    type: descriptor.type,
    fields: Object.fromEntries(
      Object.entries(descriptor.fields).map(([key, value]) => [
        key,
        { ...value },
      ]),
    ) as Record<ChamferPropertyFieldKey, ChamferPropertyFieldDescriptor>,
  };
}

/**
 * 归一化本阶段明确忽略的倒角选项。
 *
 * 设计说明：
 * 1. 用户明确要求不做切线延伸、显示选择工具栏、预览模式和倒角选项；
 * 2. 这些旧字段仍可能来自旧命令或编辑会话，必须在 cad-core 统一固定，而不是只靠 UI 隐藏；
 * 3. Face Face 的帮助点也属于当前被忽略的高级选项，因此新建/编辑/重建都不再消费旧值。
 *
 * @param definition 原始倒角定义。
 * @returns 固定忽略项后的倒角定义。
 */
export function normalizeChamferIgnoredOptions(
  definition: ChamferFeatureDefinition,
): ChamferFeatureDefinition {
  const normalized = cloneChamferFeatureDefinitionData(definition);
  return {
    ...normalized,
    tangentPropagation: false,
    selectThroughFaces: false,
    keepFeatures: true,
    helpPoint: null,
  };
}
